import { requestStatus } from 'src/lib/services/api/request-api';
import { Href } from 'src/components/ui/href';
import { RateRevisionComposedDialog } from 'src/features/revisions/rate-revision-composed-dialog';
import { DialogTrigger } from 'src/components/ui/dialog';
import { If } from 'src/components/If';
import { authQueries } from 'src/api/queries';
import { useDecoratedPostHog, customPosthogEvent } from 'src/providers/posthog';
import { requestTrustpilotReview } from 'src/lib/services/user-guiding/trustpilot-review-guide';
import { useQueryClient } from '@tanstack/react-query';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import { useRequestsCounts } from 'src/models';
import { useConfirm } from 'src/components/ConfirmModal';
import { useChatToast } from 'src/components/chat-toast/chat-toast';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useRequestMutations } from 'src/features/requests/use-request-mutations';
import { useToast } from 'src/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/ui/button/button';
import { ButtonLoader } from 'src/components/ui/loader';
import { CheckCircle, Megaphone } from 'lucide-react';
import React from 'react';
import { ConfirmPopover } from 'src/components/ui/confirm-popover';

// Stable anchors for the UserGuiding onboarding tour. UserGuiding pins a guide
// step to a DOM element; with no explicit id it auto-captures a CSS path built
// out of this button's utility classes, which silently stops matching the next
// time the layout or styling changes — the tour just quietly stops appearing.
//
// Each id is deliberately reused across two MUTUALLY EXCLUSIVE branches below,
// so that a single anchor matches every client regardless of which variant they
// render. Do not split them into per-branch ids — a tour anchored to one variant
// silently skips the clients who see the other, which is the bug these ids exist
// to fix.
//
// Why only one of each pair can ever be in the document at a time — both flags
// come from decorate-request.tsx (via useDecoratedRequestContext), NOT from
// models/request/useRequest.tsx, which defines same-named flags differently:
//
//   Re-edit — structurally exclusive: the two variants are the branches of one
//             If/else on `canRateRevision`.
//   Approve — exclusive by invariant, not by structure. `canApprove` requires
//             `needsApproval && !isApproved`; when `needsApproval` is true
//             `canComplete` requires `isApproved`. So the two cannot both hold.
//
// That Approve invariant is load-bearing and lives in another file — if it is
// ever broken, two nodes share this id and UserGuiding anchors the tooltip to
// whichever comes first in the DOM. There is a matching note beside those two
// flags in decorate-request.tsx.
const REEDIT_BUTTON_ID = 'request-reedit-button';
const APPROVE_BUTTON_ID = 'request-approve-button';

const ReEditAndApproveButtons: React.FC<{
  rateRevisionDialogOpen: boolean;
  setRateRevisionDialogOpen: (open: boolean) => void;
}> = ({ rateRevisionDialogOpen, setRateRevisionDialogOpen }) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const request = useDecoratedRequestContext();
  const requestMutations = useRequestMutations();
  const postHog = useDecoratedPostHog();
  const client = useQueryClient();
  const { push } = useChatToast();
  const { update } = useRequestsCounts(
    {},
    {
      enabled: false,
    },
  );
  const { confirm } = useConfirm();

  // latest revision (server data)
  const latestRevision = [...(request?.revisions ?? [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )?.[0];

  // Shared identity for every rating-related PostHog event, so a funnel joining
  // them stays joinable if these dimensions ever change.
  const ratingEventDimensions = (rating: number) => {
    const profile = client.getQueryData<any>([authQueries.profile]);
    const company = profile?.company;

    return {
      company: {
        id: company?.id ?? 'unknown',
        name: company?.name ?? 'Unknown',
      },
      request: {
        id: request.id,
        name: request.title ?? 'Unknown',
      },
      requestRevisionRating: rating,
    };
  };

  const capturePosthogEvent = (rating: number) => {
    postHog.capture(customPosthogEvent.requestRevisionRated, {
      ...ratingEventDimensions(rating),
      editor:
        joinExistingValues([request?.editor?.first_name, request?.editor?.last_name], ' ') ??
        'Unknown',
    });
  };

  const updateRequestQueryData = (rating: number) => {
    requestMutations.setQueryData(request.id, (prev) => {
      if (!prev) return prev;

      const next = { ...prev } as typeof prev & {
        rated_revisions_count?: number;
        revisions: Array<{ id: string; rating?: number } & any>;
      };

      next.rated_revisions_count = (next.rated_revisions_count ?? 0) + 1;

      if (next.revisions?.length) {
        next.revisions = next.revisions.map((rev: any) => {
          if (rev.id === latestRevision?.id) {
            return { ...rev, rating };
          }
          return rev;
        });
      }

      return next;
    });
  };

  const handleRevisionRated = (rating: number) => {
    capturePosthogEvent(rating);
    updateRequestQueryData(rating);

    // 4-5 star ratings ask the client for a TrustPilot review. This runs after
    // the rate call resolved and after the dialog closed (the star sits inside
    // a DialogClose), so a failed rating never asks, and the guide does not
    // fight the dialog for the overlay.
    if (requestTrustpilotReview(rating)) {
      // Asked, not necessarily shown — UserGuiding's frequency rules may still
      // suppress it. Whether clients actually saw and acted on the guide is
      // measured in UserGuiding; this only tells us how often we asked.
      postHog.capture(
        customPosthogEvent.trustpilotReviewGuideTriggered,
        ratingEventDimensions(rating),
      );
    }
  };

  const handleRequestReedit = async () => {
    requestMutations.requestReedit.mutate(request.id, {
      onSuccess: () => {
        push({
          description: "Don't forget to leave a comment for your editor!",
          variant: 'success',
          mode: 'trigger',
        });
        if (request.canRateRevision) setRateRevisionDialogOpen(true);
      },
    });
  };

  return (
    <div className={'tw-flex tw-justify-end tw-gap-2'}>
      {/* Request re-edit / Rate revision flow */}
      <If
        when={request.canRateRevision}
        else={
          <If when={request.canRequestReEdit}>
            <Button
              id={REEDIT_BUTTON_ID}
              size={'default'}
              className={'tw-gap-2 tw-whitespace-nowrap'}
              disabled={requestMutations.isAnyMutationLoading}
              onClick={handleRequestReedit}
            >
              <Megaphone className={'tw-size-4'} />
              {t('Re-edit')}
              <ButtonLoader loading={requestMutations.requestReedit.isLoading} />
            </Button>
          </If>
        }
      >
        <RateRevisionComposedDialog
          revision={latestRevision?.id}
          onSuccess={handleRevisionRated}
          open={rateRevisionDialogOpen}
          onOpenChange={setRateRevisionDialogOpen}
        >
          <If when={request.canRequestReEdit}>
            <DialogTrigger asChild>
              <Button
                id={REEDIT_BUTTON_ID}
                size={'sm'}
                className={'tw-flex-grow tw-gap-2 tw-whitespace-nowrap'}
                disabled={requestMutations.requestReedit.isLoading}
                onClick={handleRequestReedit}
              >
                <Megaphone className={'tw-size-4'} />
                {t('Re-edit')}
              </Button>
            </DialogTrigger>
          </If>
        </RateRevisionComposedDialog>
      </If>

      {/* Approve */}
      <If when={request.canApprove}>
        <ConfirmPopover
          title={t('alerts:confirm.approve_request.title', {
            defaultValue: 'Approve this request?',
          }) ?? undefined}
          confirmText={t('alerts:confirm.approve_request.confirm', {
            defaultValue: 'Yes, Approve',
          }) ?? undefined}
          confirmVariant="success"
          disabled={requestMutations.isAnyMutationLoading}
          isLoading={requestMutations.approve.isLoading}
          onConfirm={() =>
            requestMutations.approve.mutate(request.id, {
              onSuccess: () => {
                toast({
                  description: t('alerts:success.request_approved', {
                    defaultValue: 'Request has been approved!',
                  }),
                });
              },
              onError: () => {
                toast({
                  variant: 'destructive',
                  description: t('alerts:error.request_approval_failed', {
                    defaultValue: 'Failed to approve request. Please try again.',
                  }),
                });
              },
            })
          }
        >
          <Button
            id={APPROVE_BUTTON_ID}
            variant={'success'}
            size={'default'}
            className={'tw-whitespace-nowrap'}
            disabled={requestMutations.isAnyMutationLoading}
          >
            {t('Approve')}
            <ButtonLoader loading={requestMutations.approve.isLoading} />
          </Button>
        </ConfirmPopover>
      </If>

      {/* Complete request */}
      <If when={request.canComplete}>
        <Button
          id={APPROVE_BUTTON_ID}
          variant={'success'}
          size={'sm'}
          className={'tw-flex-grow tw-gap-2 tw-whitespace-nowrap'}
          disabled={requestMutations.isAnyMutationLoading || !request.canComplete}
          onClick={async () => {
            const confirmed = await confirm('', {
              modal_header: {
                content: t('alerts:confirm.complete_request.title', {
                  defaultValue: 'Approve',
                })!,
              },
              modal_body: {
                content: (
                  <span>
                    {t('alerts:confirm.complete_request_v2', {
                      //this is temporary, fix this directly in the translation service later
                      defaultValue:
                        'Approving will stop all edits and comments. You can still view and download files for 1 month.',
                    })}{' '}
                    <Href
                      href={
                        t('links:other.request_approval_learn_more.url', {
                          defaultValue:
                            'https://help.becreatives.co/what-happens-when-i-complete-my-request',
                        })!
                      }
                      target={'_blank'}
                      variant={'sky'}
                      underline={'hover'}
                    >
                      {t('Read more')}
                    </Href>
                  </span>
                ),
              },
            });

            if (!confirmed) return;

            requestMutations.complete.mutate(request.id, {
              onSuccess: () => {
                update(request.id, requestStatus.complete);
                toast({
                  description: t('alerts:success.request_completed', {
                    defaultValue: 'Request has been completed!',
                  }),
                });
              },
            });
          }}
        >
          <CheckCircle className={'tw-size-4'} />
          {t('Approve')}
          <ButtonLoader loading={requestMutations.complete.isLoading} />
        </Button>
      </If>
    </div>
  );
};

export default ReEditAndApproveButtons;
